import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Slot group definitions ─────────────────────────────────────────────────
// Two independent CTA link groups so operators can retarget URLs / labels
// without touching component code. Both carry a `_target` so richtext links
// stay policy-compliant (URL-bearing groups require it).

const applyCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA (Apply Now)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA (Contact Leasing)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Leasing',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function PepCallToAction() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const applyCta = useSlotGroup('pepcalltoaction.apply_cta', applyCtaDef);
  const contactCta = useSlotGroup('pepcalltoaction.contact_cta', contactCtaDef);

  return (
    <section ref={sectionRef} className="reveal-fade-up pep-cta">
      <div className="pep-cta-container">
        <RichTextContent
          path="pepcalltoaction.heading"
          defaultValue={
            '<p>Take advantage of your Preferred Employer benefits at ' +
            '<span data-type="field-token" data-field="property_name"></span>.</p>'
          }
          as="h2"
          className="pep-cta-heading"
        />
        <RichTextContent
          path="pepcalltoaction.description"
          defaultValue="<p>Apply online today or reach out to our leasing team to verify eligibility and confirm the latest move-in offer.</p>"
          as="p"
          className="pep-cta-description"
        />

        <div className="pep-cta-actions">
          <EditableSlotGroup groupPath="pepcalltoaction.apply_cta" definition={applyCtaDef}>
            <a
              href={applyCta.url || '/apply-now'}
              target={applyCta._target || '_self'}
              rel={applyCta._target === '_blank' ? 'noopener noreferrer' : undefined}
              className="pep-cta-btn pep-cta-btn--primary"
            >
              <span className="pep-cta-btn-label">{applyCta.label || 'Apply Now'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="pepcalltoaction.contact_cta" definition={contactCtaDef}>
            <a
              href={contactCta.url || '#'}
              target={contactCta._target || '_self'}
              rel={contactCta._target === '_blank' ? 'noopener noreferrer' : undefined}
              className="pep-cta-btn pep-cta-btn--secondary"
            >
              <span className="pep-cta-btn-label">{contactCta.label || 'Contact Leasing'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pep-cta {
          padding: 4.5rem 0 5rem;
          background: var(--vibe-color-primary, #2b2b2b);
          color: var(--vibe-color-text-on-primary, #ffffff);
        }
        @media (min-width: 768px) {
          .pep-cta { padding: 6rem 0 6.5rem; }
        }

        .pep-cta-container {
          max-width: 62rem;
          margin: 0 auto;
          padding: 0 1rem;
          text-align: center;
        }
        @media (min-width: 640px) { .pep-cta-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .pep-cta-container { padding: 0 2rem; } }

        .pep-cta-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 700;
          line-height: 1.15;
          letter-spacing: 0.01em;
          margin: 0 0 1.25rem;
          color: inherit;
        }
        @media (min-width: 768px) {
          .pep-cta-heading { font-size: 2.75rem; }
        }
        @media (min-width: 1024px) {
          .pep-cta-heading { font-size: 3.25rem; }
        }

        .pep-cta-description {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          margin: 0 auto 2.25rem;
          max-width: 36rem;
          color: color-mix(in srgb, currentColor 85%, transparent);
        }
        @media (min-width: 768px) {
          .pep-cta-description { font-size: 1.0625rem; }
        }

        .pep-cta-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.875rem;
        }
        @media (min-width: 640px) {
          .pep-cta-actions { flex-direction: row; gap: 1rem; }
        }

        .pep-cta-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          /* Shared minimum so the two CTAs read as a matched pair regardless
             of label length. Padding still absorbs slightly longer labels. */
          min-width: 15rem;
          box-sizing: border-box;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.22em;
          padding: 1rem 2.25rem;
          border-radius: 2px;
          text-decoration: none;
          cursor: pointer;
          transition: color 0.3s ease, background 0.3s ease, transform 0.3s ease,
            box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .pep-cta-btn-label {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* Primary — inverted over the primary-color surface: fills with the
           on-primary token so the button reads as a light chip with accent text. */
        .pep-cta-btn--primary {
          background: var(--vibe-color-text-on-primary, #ffffff);
          color: var(--vibe-color-primary);
          border: 1.5px solid var(--vibe-color-text-on-primary, #ffffff);
        }
        .pep-cta-btn--primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 90%, #000);
        }
        .pep-cta-btn--primary:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Secondary — outline over the primary surface, fills with the
           on-primary token on hover so the label flips to the secondary color. */
        .pep-cta-btn--secondary {
          background: transparent;
          color: currentColor;
          border: 1.5px solid color-mix(in srgb, currentColor 65%, transparent);
        }
        .pep-cta-btn--secondary::before {
          content: '';
          position: absolute;
          inset: 0;
          /* Fixed on-primary fill — must NOT be currentColor, because on
             hover the label color transitions to secondary and would drag
             the pseudo-element fill with it, making it match the section
             background and hide the label. */
          background: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .pep-cta-btn--secondary:hover {
          border-color: var(--vibe-color-text-on-primary, #ffffff);
          /* Wash is on-primary, so flip the label to the secondary accent for
             AA contrast against it. */
          color: var(--vibe-color-secondary);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
        }
        .pep-cta-btn--secondary:hover::before { opacity: 1; }
        .pep-cta-btn--secondary:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ──────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PepCallToAction',
  displayName: 'PEP – Call To Action',
  tokens: {
    'pepcalltoaction.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Take advantage of your Preferred Employer benefits at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'pepcalltoaction.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Apply online today or reach out to our leasing team to verify eligibility and confirm the latest move-in offer.</p>',
    },
  },
  slotGroups: {
    'pepcalltoaction.apply_cta': applyCtaDef,
    'pepcalltoaction.contact_cta': contactCtaDef,
  },
};

(PepCallToAction as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PepCallToAction;
